import { useQuery } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { CenteredLoader, Checkbox } from '@/components_deprecated'
import { FilterPopup } from '@/components_deprecated/FilterPopup'
import {
  FilterCheckmarksWrapper,
  StyledScrollBar,
} from '@/components_deprecated/FilterPopup/components'
import { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useAdmin } from '@/utils/hooks'
import type { AllUserDocumentCategoriesQuery } from '..'
import { ALL_USER_DOCUMENT_CATEGORIES_QUERY } from '../queries'

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  & > * + * {
    margin-top: 0.3rem;
  }
  margin-top: 1rem;
`

const Divider = styled.div`
  width: 100%;
  border-bottom: 1px solid ${props => props.theme.colors.gray8};
  margin: 1rem 0;
`

const StyledPopup = styled(FilterPopup)`
  margin-bottom: 2rem;
`

const StyledCheckbox = styled(Checkbox)`
  font-weight: 500;
`

interface UserDocumentCategoryFilterProps {
  showDeactivated: boolean
  includeExpiredDocuments: boolean
  onSelectionChange: (categories: string[]) => void
  onShowDeactivatedChange: (showDeactivated: boolean) => void
  onIncludeExpiredDocumentsChange: (includeExpiredDocuments: boolean) => void
}

export const UserDocumentCategoryFilter: React.FC<
  UserDocumentCategoryFilterProps
> = ({
  showDeactivated,
  includeExpiredDocuments,
  onSelectionChange,
  onShowDeactivatedChange,
  onIncludeExpiredDocumentsChange,
}) => {
  const translations = useTranslate({
    showDeactivated: 'users.show-deactivated',
    includeExpiredDocuments: 'users.include-expired-documents',
    hasDocument: 'users.has-document',
  })

  const admin = useAdmin()

  const { data, loading } = useQuery<
    AllUserDocumentCategoriesQuery,
    { includeExpiredDocuments: boolean }
  >(ALL_USER_DOCUMENT_CATEGORIES_QUERY, {
    variables: { includeExpiredDocuments },
  })

  const categories = useMemo(
    () => data?.allUserDocumentCategories.edges.map(edge => edge.node) ?? [],
    [data?.allUserDocumentCategories.edges]
  )

  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  function toggleCategory(id: string) {
    const newCategories = selectedCategories.includes(id)
      ? selectedCategories.filter(iid => iid !== id)
      : [...selectedCategories, id]
    setSelectedCategories(newCategories)
    onSelectionChange(newCategories)
  }
  return (
    <StyledPopup>
      {admin && (
        <StyledDiv>
          <Checkbox
            checked={showDeactivated}
            label={translations.showDeactivated}
            boxWidth="32px"
            boxHeight="32px"
            fontSize="1rem"
            onChange={() => onShowDeactivatedChange(!showDeactivated)}
          />
        </StyledDiv>
      )}

      {categories.length !== 0 && <h4>{translations.hasDocument}</h4>}

      <FilterCheckmarksWrapper>
        {loading && <CenteredLoader marginTop="1rem" marginBottom="1rem" />}

        <StyledScrollBar>
          {categories.map(category => {
            const checked = selectedCategories.includes(category.id)

            return (
              <StyledCheckbox
                key={category.id}
                checked={checked}
                label={`${category.name} (${category.numUsers})`}
                boxWidth="32px"
                boxHeight="32px"
                fontSize="1rem"
                onChange={() => toggleCategory(category.id)}
              />
            )
          })}

          <Divider />

          <StyledCheckbox
            key="expired"
            checked={includeExpiredDocuments}
            label={translations.includeExpiredDocuments}
            boxWidth="32px"
            boxHeight="32px"
            fontSize="1rem"
            onChange={value => onIncludeExpiredDocumentsChange(value)}
          />
        </StyledScrollBar>
      </FilterCheckmarksWrapper>
    </StyledPopup>
  )
}
